import React, { useEffect, useState } from "react";
import styled from "styled-components";
import api from "../services/api";
import EditUserModal from "../components/Admin/EditUserModal";
import { useTheme } from "../contexts/ThemeContext";

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1rem;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const Title = styled.h1`
  color: ${props => props.theme.primaryText};
  margin-bottom: 2rem;
  transition: color 0.3s ease;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    overflow-x: visible;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  background: ${props => props.theme.cardBg};
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.3s ease;
  
  tbody tr {
    &:nth-child(odd) {
      background-color: ${props => props.theme.tableRowBg};
    }
    
    &:nth-child(even) {
      background-color: ${props => props.theme.tableRowHoverBg};
    }
    
    &:hover {
      background-color: ${props => props.theme.tertiaryBg};
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeader = styled.th`
  border-bottom: 2px solid ${props => props.theme.border};
  text-align: left;
  padding: 0.75rem;
  background: ${props => props.theme.tableHeaderBg};
  color: ${props => props.theme.primaryText};
  font-weight: 600;
  transition: all 0.3s ease;
  
  &.sticky {
    position: sticky;
    left: 0;
    z-index: 10;
    background: ${props => props.theme.tableHeaderBg};
  }
  
  &.center {
    text-align: center;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.primaryText};
  transition: all 0.3s ease;
  
  &.sticky {
    position: sticky;
    left: 0;
    background: inherit;
    z-index: 5;
  }
  
  &.center {
    text-align: center;
  }
`;

const EmailContainer = styled.div`
  .email {
    font-weight: bold;
    color: ${props => props.theme.primaryText};
  }
  
  .created {
    font-size: 0.8rem;
    color: ${props => props.theme.secondaryText};
  }
`;

const DateContainer = styled.div`
  .date {
    color: ${props => props.theme.primaryText};
  }
  
  .relative {
    font-size: 0.8rem;
    color: ${props => props.theme.secondaryText};
  }
`;

const Badge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  transition: all 0.3s ease;
  
  &.login-count {
    background-color: ${props => props.hasLogins ? props.theme.success : props.theme.secondaryBg};
    color: ${props => props.hasLogins ? props.theme.inverseText : props.theme.secondaryText};
  }
  
  &.premium {
    background-color: ${props => props.theme.success};
    color: ${props => props.theme.inverseText};
  }
  
  &.free {
    background-color: ${props => props.theme.secondaryBg};
    color: ${props => props.theme.secondaryText};
  }
  
  &.admin {
    background-color: ${props => props.theme.warning};
    color: ${props => props.theme.inverseText};
  }
  
  &.user {
    background-color: ${props => props.theme.secondaryBg};
    color: ${props => props.theme.secondaryText};
  }
`;

const EditButton = styled.button`
  padding: 0.5rem 1rem;
  margin-right: 0.5rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.primaryHover};
    color: ${props => props.theme.inverseText};
  }
`;

const DeleteButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.theme.error};
  border-radius: 4px;
  background-color: transparent;
  color: ${props => props.theme.error};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.error};
    color: ${props => props.theme.inverseText};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryContainer = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: ${props => props.theme.secondaryBg};
  border-radius: 8px;
  transition: background-color 0.3s ease;
`;

const SummaryTitle = styled.h3`
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  
  > div {
    color: ${props => props.theme.primaryText};
    transition: color 0.3s ease;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.25rem;
  color: ${props => props.theme.secondaryText};
  transition: color 0.3s ease;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.25rem;
  color: ${props => props.theme.error};
  transition: color 0.3s ease;
`;

// Mobile Card Components
const MobileCardContainer = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    margin-top: 2rem;
  }
`;

const UserCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${props => props.theme.shadowHover};
  }
`;

const UserCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const UserEmail = styled.div`
  .email {
    font-weight: bold;
    color: ${props => props.theme.primaryText};
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }
  
  .created {
    font-size: 0.8rem;
    color: ${props => props.theme.secondaryText};
  }
`;

const UserName = styled.div`
  color: ${props => props.theme.primaryText};
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const UserInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const UserInfoItem = styled.div`
  .label {
    font-size: 0.8rem;
    color: ${props => props.theme.secondaryText};
    margin-bottom: 0.25rem;
  }
  
  .value {
    color: ${props => props.theme.primaryText};
    font-weight: 500;
  }
`;

const UserStatusRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MobileActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

/**
 * AdminUsersPage displays a list of all users for admin management.
 * Only accessible to admin users.
 */
const AdminUsersPage = () => {
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get("/admin/users");
        setUsers(res.data.users || []);
      } catch (err) {
        setError("Failed to load users.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  const handleUserUpdated = (updatedUser) => {
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === updatedUser.id ? updatedUser : user
      )
    );
  };

  const handleDeleteUser = async (user) => {
    if (user.is_admin) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete ${user.email}? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete user");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) return <LoadingMessage theme={theme}>Loading users...</LoadingMessage>;
  if (error) return <ErrorMessage theme={theme}>{error}</ErrorMessage>;

  return (
    <Container theme={theme}>
      <Title theme={theme}>Admin - User Management</Title>
      
      <SummaryContainer theme={theme}>
        <SummaryTitle theme={theme}>Summary</SummaryTitle>
        <SummaryGrid theme={theme}>
          <div>
            <strong>Total Users:</strong> {users.length}
          </div>
          <div>
            <strong>Premium Users:</strong> {users.filter(u => u.is_premium).length}
          </div>
          <div>
            <strong>Active Users (logged in last 30 days):</strong> {
              users.filter(u => {
                if (!u.last_login) return false;
                const lastLogin = new Date(u.last_login);
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                return lastLogin > thirtyDaysAgo;
              }).length
            }
          </div>
          <div>
            <strong>New Users (last 30 days):</strong> {
              users.filter(u => {
                if (!u.member_since) return false;
                const memberSince = new Date(u.member_since);
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                return memberSince > thirtyDaysAgo;
              }).length
            }
          </div>
          <div>
            <strong>📬 Picks Subscribers:</strong> {users.filter(u => u.email_subscribed).length}
          </div>
          <div>
            <strong>🏆 Results Subscribers:</strong> {users.filter(u => u.email_results_subscribed).length}
          </div>
        </SummaryGrid>
      </SummaryContainer>
      
      <TableContainer>
        <Table theme={theme}>
          <thead>
            <tr>
              <TableHeader theme={theme} className="sticky">Email</TableHeader>
              <TableHeader theme={theme}>Name</TableHeader>
              <TableHeader theme={theme}>Member Since</TableHeader>
              <TableHeader theme={theme}>Last Login</TableHeader>
              <TableHeader theme={theme} className="center">Logins</TableHeader>
              <TableHeader theme={theme} className="center">Premium</TableHeader>
              <TableHeader theme={theme} className="center">Admin</TableHeader>
              <TableHeader theme={theme} className="center">Subs</TableHeader>
              <TableHeader theme={theme}>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <TableCell theme={theme} className="sticky">
                  <EmailContainer theme={theme}>
                    <div className="email">{user.email}</div>
                    <div className="created">
                      Created: {formatDate(user.created_at)}
                    </div>
                  </EmailContainer>
                </TableCell>
                <TableCell theme={theme}>
                  {user.first_name} {user.last_name}
                </TableCell>
                <TableCell theme={theme}>
                  <DateContainer theme={theme}>
                    <div className="date">{formatDate(user.member_since)}</div>
                    <div className="relative">
                      {formatRelativeTime(user.member_since)}
                    </div>
                  </DateContainer>
                </TableCell>
                <TableCell theme={theme}>
                  <DateContainer theme={theme}>
                    <div className="date">{formatDate(user.last_login)}</div>
                    <div className="relative">
                      {formatRelativeTime(user.last_login)}
                    </div>
                  </DateContainer>
                </TableCell>
                <TableCell theme={theme} className="center">
                  <Badge 
                    theme={theme} 
                    className="login-count" 
                    hasLogins={user.login_count > 0}
                  >
                    {user.login_count}
                  </Badge>
                </TableCell>
                <TableCell theme={theme} className="center">
                  {user.is_premium ? (
                    <Badge theme={theme} className="premium">
                      Premium
                    </Badge>
                  ) : (
                    <Badge theme={theme} className="free">
                      Free
                    </Badge>
                  )}
                </TableCell>
                <TableCell theme={theme} className="center">
                  {user.is_admin ? (
                    <Badge theme={theme} className="admin">
                      Admin
                    </Badge>
                  ) : (
                    <Badge theme={theme} className="user">
                      User
                    </Badge>
                  )}
                </TableCell>
                <TableCell theme={theme} className="center">
                  <span title={`Picks: ${user.email_subscribed ? 'On' : 'Off'} · Results: ${user.email_results_subscribed ? 'On' : 'Off'}`} style={{ fontSize: '0.85rem', display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                    <span style={{ opacity: user.email_subscribed ? 1 : 0.3 }} title="Daily Picks emails">📬</span>
                    <span style={{ opacity: user.email_results_subscribed ? 1 : 0.3 }} title="Results emails">🏆</span>
                  </span>
                </TableCell>
                <TableCell theme={theme}>
                  <EditButton 
                    theme={theme}
                    onClick={() => handleEditUser(user)}
                  >
                    Edit
                  </EditButton>
                  <DeleteButton
                    theme={theme}
                    disabled={user.is_admin}
                    onClick={() => handleDeleteUser(user)}
                  >
                    Delete
                  </DeleteButton>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      
      {/* Mobile Card View */}
      <MobileCardContainer>
        {users.map((user) => (
          <UserCard key={user.id} theme={theme}>
            <UserCardHeader theme={theme}>
              <UserEmail theme={theme}>
                <div className="email">{user.email}</div>
                <div className="created">
                  Created: {formatDate(user.created_at)}
                </div>
              </UserEmail>
            </UserCardHeader>
            
            <UserName theme={theme}>
              {user.first_name} {user.last_name}
            </UserName>
            
            <UserInfoGrid>
              <UserInfoItem theme={theme}>
                <div className="label">Member Since</div>
                <div className="value">{formatDate(user.member_since)}</div>
                <div className="label">{formatRelativeTime(user.member_since)}</div>
              </UserInfoItem>
              
              <UserInfoItem theme={theme}>
                <div className="label">Last Login</div>
                <div className="value">{formatDate(user.last_login)}</div>
                <div className="label">{formatRelativeTime(user.last_login)}</div>
              </UserInfoItem>
            </UserInfoGrid>
            
            <UserStatusRow>
              <Badge 
                theme={theme} 
                className="login-count" 
                hasLogins={user.login_count > 0}
              >
                Logins: {user.login_count}
              </Badge>
              
              {user.is_premium ? (
                <Badge theme={theme} className="premium">
                  Premium
                </Badge>
              ) : (
                <Badge theme={theme} className="free">
                  Free
                </Badge>
              )}
              
              {user.is_admin ? (
                <Badge theme={theme} className="admin">
                  Admin
                </Badge>
              ) : (
                <Badge theme={theme} className="user">
                  User
                </Badge>
              )}
              <span style={{ fontSize: '0.85rem', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                <span style={{ opacity: user.email_subscribed ? 1 : 0.3 }} title="Daily Picks emails">📬</span>
                <span style={{ opacity: user.email_results_subscribed ? 1 : 0.3 }} title="Results emails">🏆</span>
              </span>
            </UserStatusRow>
            
            <MobileActions>
              <EditButton 
                theme={theme}
                onClick={() => handleEditUser(user)}
              >
                Edit
              </EditButton>
              <DeleteButton
                theme={theme}
                disabled={user.is_admin}
                onClick={() => handleDeleteUser(user)}
              >
                Delete
              </DeleteButton>
            </MobileActions>
          </UserCard>
        ))}
      </MobileCardContainer>
      
      <EditUserModal
        user={editingUser}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onUserUpdated={handleUserUpdated}
      />
    </Container>
  );
};

export default AdminUsersPage; 